import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'

import { defaultRfqFormParams } from '@/features/rfq/lib/rfq-draft'
import type {
  MakerListsReceived,
  MakerUnavailable,
  PendingMakerQuote,
} from '@/features/rfq/lib/rfq-maker-store'
import { RfqFormParams } from '@/features/rfq/types'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import type { DockPoint } from '@/lib/floating-dock/dock-position'
import { safeStorage } from '@/lib/safe-storage'
import { RfqAccept, RfqMakerQuote, RfqMakerRfq, RfqUpdate } from '@/types/generated/rfq'

/**
 * Live RFQ snapshots seen on the account RFQ stream this session, keyed by
 * `(rfqId, rfqNetwork)` (upserted via `upsertRfqs`) — one `rfqId` can hold a live row on
 * Deribit and on Paradigm at once, so this is a flat list of per-network rows, NOT one
 * row per RFQ. Group it with `buildRfqCards` before displaying anything. The account RFQ
 * stream sends no snapshot on subscribe, so seed the displayed list from
 * `get_rfq_history` and merge this atom on top.
 */
export const rfqsRawAtom = atom<RfqUpdate[]>([])

/** Every active account's live RFQ stream frames, unfiltered — mirrors allOpenOrdersAtom
 * etc. in state/account.ts. */
export const allRfqsAtom = atom((get) => get(rfqsRawAtom))

/**
 * Latest venue-confirmed acceptance per `rfqId` (from the `rfq_accept` stream frame),
 * used for an instant "accepted @ price" affirmation before the FILLED snapshot lands.
 * Keyed by `rfqId` alone on purpose: only one network is ever accepted for a given RFQ
 * (the algo's `acceptIntent` guards against a second), and the frame names its own
 * network in `RfqAccept.rfqNetwork` for display.
 */
export const rfqAcceptsAtom = atom<Record<string, RfqAccept>>({})

/**
 * Per-card RFQ-only params (makers, disclosure, manual/auto, hedge) for the order-form
 * RFQ mode. The legs live in the shared order-form items; these are merged with them via
 * `composeRfqDraft` at submit. Cleaned up alongside the other order atoms.
 */
export const rfqFormParamsAtomFamily = atomFamily((_cardId: string) =>
  atom<RfqFormParams>(defaultRfqFormParams())
)

/**
 * Persisted top-left position (px) of a draggable RFQ quote popup, one per `rfqId`.
 * The storage key embeds the id so multiple simultaneously-open popups do not read and
 * write the same slot, clobbering each other's position.
 */
export const rfqPopupPositionAtomFamily = atomFamily((rfqId: string) =>
  atomWithStorage<DockPoint | null>(STORAGE_KEYS.rfq.popupPosition(rfqId), null, safeStorage, {
    getOnInit: true,
  })
)

/**
 * Account-wide list of `rfqId`s currently showing a draggable quote-accept popup
 * (GlobalRfqPopups, mounted once in DashboardLayout — see rfq-popup-item.tsx). Keyed by
 * rfqId rather than the submitting order-form card's cardId so a popup survives that
 * card's widget being closed, its watchlist selection changing (which used to unmount
 * the old per-card OrderFormContent-nested popup), or the order-form-sheet closing.
 */
export const activeRfqPopupIdsAtom = atom<string[]>([])

/**
 * The accountId that actually submitted each open RFQ popup, keyed by `rfqId` —
 * a snapshot taken from the create-RFQ mutation's own `variables` at submit
 * time (see order-form-summary.tsx's `useCreateRfq` `onSuccess`), NOT derived
 * live from the card/tab that submitted it. The popup can outlive its
 * originating tab/card entirely, so this is the only correct source of truth
 * for "which account does this RFQ belong to." Entries are removed alongside
 * `activeRfqPopupIdsAtom`'s own removal (see rfq-popup-item.tsx's removeSelf).
 */
export const rfqPopupAccountIdAtom = atom<Record<string, string>>({})

// ─── Maker side (RFQ_MAKER stream) ─────────────────────────────────────────

/**
 * Incoming RFQs raised by other takers, keyed `accountId|rfqId` (`makerRfqKey`): the same
 * venue RFQ reaches every maker account on that venue. Upserted via `upsertMakerRfqs`.
 */
export const makerRfqsAtom = atom<Record<string, RfqMakerRfq>>({})

/** This user's own maker quotes, keyed by quote_id. Upserted via `upsertMakerQuotes`. */
export const makerQuotesAtom = atom<Record<string, RfqMakerQuote>>({})

/** Accounts the venue refused as a maker, keyed by makerKey (`applyMakerAvailability`). */
export const makerUnavailableAtom = atom<Record<string, MakerUnavailable>>({})

/** Venue-side maker name per makerKey (`applyMakerDisplayName`); kept across resubscribes. */
export const makerDisplayNamesAtom = atom<Record<string, string>>({})

/** makerKeys that have answered the current RFQ_MAKER subscribe (`applyMakerListReceived`). */
export const makerListsReceivedAtom = atom<MakerListsReceived>({})

/**
 * Sends the server has routed but the stream has not answered yet, keyed by `quoteSlotKey`.
 * Drives "sending…" and keeps a retry on the same quote_id (`resolveSendQuoteId`).
 */
export const pendingMakerQuotesAtom = atom<Record<string, PendingMakerQuote>>({})

/** Clear user-owned maker data when the client session ends, including pending sends. */
export const clearRfqMakerRuntimeAtom = atom(null, (_get, set) => {
  set(makerRfqsAtom, {})
  set(makerQuotesAtom, {})
  set(makerUnavailableAtom, {})
  set(makerDisplayNamesAtom, {})
  set(makerListsReceivedAtom, {})
  set(pendingMakerQuotesAtom, {})
})
